import { Instagram, Mail, ChevronDown, ChevronUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';

const FOOTER_LINKS = [
  { label: "About Us", href: "/about" },
  { label: "Contact", href: "/contact" },
  { label: "FAQs", href: "/faqs" },
  { label: "Shipping", href: "/shipping" },
  { label: "Privacy Policy", href: "/privacy" },
  { label: "Terms", href: "/terms" }
];

const SEO_LANDING_PAGES = [
  { label: "Flowers Sydney", href: "/flowers-sydney" },
  { label: "Same Day Delivery", href: "/same-day-flower-delivery" },
  { label: "Sydney Florists", href: "/florists-sydney" },
  { label: "Local Florists", href: "/local-florists" },
  { label: "Flower Delivery", href: "/flower-delivery-sydney" }
];

const SUBURB_PAGES = [
  { label: "Newtown", href: "/florists-newtown" },
  { label: "Bondi", href: "/florists-bondi" },
  { label: "Surry Hills", href: "/florists-surry-hills" },
  { label: "Parramatta", href: "/florists-parramatta" },
  { label: "Marrickville", href: "/florists-marrickville" }
];

export function Footer() {
  const footerRef = useRef<HTMLDivElement>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  
  useEffect(() => {
    // Check if another footer already exists in the DOM
    const footers = document.querySelectorAll('[data-footer-id="main-footer"]');
    
    // If this is a duplicate footer (not the first one), hide it
    if (footers.length > 1 && footerRef.current) {
      footerRef.current.style.display = 'none';
    }
    
    return () => {
      // Clean up when component unmounts
      if (footerRef.current) {
        footerRef.current.style.display = 'block';
      }
    };
  }, []);

  return (
    <footer 
      ref={footerRef}
      data-footer-id="main-footer" 
      className="bg-neutral-50 border-t border-neutral-200"
    >
      <div className="max-w-[1440px] mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 md:gap-12">
          {/* Brand Column */}
          <div className="md:col-span-1">
            <Link to="/" className="text-2xl font-semibold text-brand-secondary mb-4 flex items-center satoshi-font">
              <span className="text-brand-primary">Market</span>Bloom
            </Link>
            <p className="text-sm text-neutral-600 mb-6 satoshi-font">
              Connecting you with Sydney's finest local florists
            </p>
            {/* Social */}
            <div className="flex gap-3">
              <a 
                href="https://instagram.com/marketbloom" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-[#967F81] border border-neutral-200 flex items-center justify-center text-brand-primary hover:bg-[#843329] hover:text-white hover:border-[#843329] transition-all"
              >
                <Instagram className="w-5 h-5" />
              </a>
            </div>
          </div>
          
          {/* Quick Links */}
          <div className="md:col-span-1">
            <h3 className="font-semibold text-brand-secondary mb-4 satoshi-font">Quick Links</h3>
            <nav className="flex flex-col gap-2">
              {FOOTER_LINKS.map((link) => (
                <Link 
                  key={link.href} 
                  to={link.href} 
                  className="text-sm text-neutral-600 hover:text-brand-primary transition-colors satoshi-font"
                >
                  {link.label}
                </Link>
              ))}
            </nav>
          </div>
          
          {/* Popular Searches */}
          <div className="md:col-span-1">
            <h3 className="font-semibold text-brand-secondary mb-4 satoshi-font">Popular Searches</h3>
            <nav className="flex flex-col gap-2">
              {SEO_LANDING_PAGES.map((link) => (
                <Link 
                  key={link.href} 
                  to={link.href} 
                  className="text-sm text-neutral-600 hover:text-brand-primary transition-colors satoshi-font"
                >
                  {link.label}
                </Link>
              ))}
            </nav>
          </div>
          
          {/* Contact */}
          <div className="md:col-span-1">
            <h3 className="font-semibold text-brand-secondary mb-4 satoshi-font">Get in Touch</h3>
            <div className="flex flex-col gap-3">
              <a href="mailto:info@marketbloom.com.au" className="flex items-center gap-2 text-sm text-neutral-600 hover:text-brand-primary satoshi-font">
                <Mail className="w-4 h-4" />
                <span>info@marketbloom.com.au</span>
              </a>
            </div>
          </div>
        </div>

        
        {/* Bottom section with suburbs */}
        <div className="mt-12 pt-8 border-t border-neutral-200">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
            {/* Sydney Suburbs */}
            <div>
              <h3 className="font-semibold text-brand-secondary mb-4 satoshi-font">Sydney Suburbs</h3>
              <div className="flex flex-wrap gap-x-4 gap-y-2">
                {SUBURB_PAGES.map((link) => (
                  <Link 
                    key={link.href} 
                    to={link.href} 
                    className="text-sm text-neutral-600 hover:text-brand-primary transition-colors satoshi-font"
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>
            
            {/* Newsletter Signup */}
            <div className="md:text-right">
              <h3 className="font-semibold text-brand-secondary mb-4 satoshi-font">Stay Updated</h3>
              <p className="text-sm text-neutral-600 mb-4 satoshi-font">
                Get the latest flower trends and exclusive offers
              </p>
              <Link 
                to="/contact" 
                className="inline-flex items-center justify-center px-6 py-3 text-sm font-semibold bg-[#967F81] text-white rounded-full hover:bg-[#843329] transition-all satoshi-font"
              >
                Get in Touch
              </Link>
            </div>
          </div>
          
          {/* Copyright */}
          <div className="text-center pt-8 border-t border-neutral-200">
            <p className="text-sm text-neutral-600 satoshi-font">
              © {new Date().getFullYear()} MarketBloom. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
} 